// Shared building blocks for the parsers and their analysis views: cards, rows,
// headings, notes, a searchable/sortable/exportable table, and the ✱ marker for
// anything the tool generated. Colors come from a theme (dark by default; wrap a
// light page in <AnalysisTheme.Provider value={LIGHT}>).

import React, { useState, useMemo, useEffect, createContext, useContext } from 'react';

// ─── Themes ──────────────────────────────────────────────────────────────────

export const DARK = {
  bg: '#0f172a', surface: '#1e293b', surf2: '#263044', border: '#334155',
  text: '#e2e8f0', muted: '#94a3b8', accent: '#3b82f6', accentSoft: '#93c5fd', cyan: '#67e8f9',
  warn: '#fbbf24', good: '#86efac',
  subtle: 'rgba(255,255,255,.03)', selected: 'rgba(59,130,246,.18)', heatEmpty: 'rgba(255,255,255,.04)',
  badgeBg: 'rgba(251,191,36,.08)', badgeBorder: 'rgba(251,191,36,.3)',
  explainBg: 'rgba(59,130,246,.08)', explainBorder: 'rgba(59,130,246,.35)',
  chip: { warn: 'rgba(251,191,36,.15)', good: 'rgba(134,239,172,.14)', plain: 'rgba(148,163,184,.15)' },
};
export const LIGHT = {
  bg: '#f8fafc', surface: '#ffffff', surf2: '#ffffff', border: '#e2e8f0',
  text: '#0f172a', muted: '#64748b', accent: '#2563eb', accentSoft: '#1d4ed8', cyan: '#0e7490',
  warn: '#b45309', good: '#15803d',
  subtle: '#f8fafc', selected: 'rgba(37,99,235,.10)', heatEmpty: '#f1f5f9',
  badgeBg: '#fffbeb', badgeBorder: '#fde68a',
  explainBg: '#eff6ff', explainBorder: '#bfdbfe',
  chip: { warn: '#fef3c7', good: '#dcfce7', plain: '#f1f5f9' },
};

export const AnalysisTheme = createContext(DARK);
export const useT = () => useContext(AnalysisTheme);

// Anything in these views that downloads something asks through this first, naming what:
// 'ip-lists' (IP screening's public blocklists) or 'business-listings' (the Foursquare phone
// listings). On the website it says yes straight away. The Case Analyzer desktop app, which
// blocks the internet, provides its own: it asks the investigator before going online and shows
// a "Goes online" note in place of the usual one.
export const OnlineGate = createContext({ ask: async () => true, note: null });
export const useOnlineGate = () => useContext(OnlineGate);

// Kept for pages that style their own pieces with the dark look (the Apple parser).
export const C = DARK;
export const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

export const TIME_ZONES = [
  ['local', 'Your time zone'], ['UTC', 'UTC'], ['America/New_York', 'Eastern'], ['America/Chicago', 'Central'],
  ['America/Denver', 'Mountain'], ['America/Los_Angeles', 'Pacific'],
];

export function useTimeFormat(tz) {
  return useMemo(() => {
    const f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz === 'local' ? undefined : tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZoneName: 'short',
    });
    return t => {
      if (t == null) return '';
      const p = Object.fromEntries(f.formatToParts(new Date(t)).map(x => [x.type, x.value]));
      return `${p.year}-${p.month}-${p.day} ${p.hour === '24' ? '00' : p.hour}:${p.minute}:${p.second} ${p.timeZoneName}`;
    };
  }, [tz]);
}

export function downloadCsv(name, columns, rows) {
  const esc = v => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [columns.map(c => esc(c.label)).join(','),
    ...rows.map(r => columns.map(c => esc(c.text ? c.text(r) : r[c.key])).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/[^\w.-]+/g, '_')}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const kb = n => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} B`);
export const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

export const btnStyle = t => ({
  fontSize: 12, color: t.text, background: t.surf2, border: `1px solid ${t.border}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
});
export const selectStyle = t => ({ ...btnStyle(t), padding: '5px 8px' });
export const btn = btnStyle(DARK);
export const select = selectStyle(DARK);

// Marks anything a tool computed (rankings, inferences, converted times) so it
// can't be mistaken for part of the provider's return.
export const GENERATED = 'Generated by LE Tech Tools. Not part of the original legal return.';

// ─── Small pieces ─────────────────────────────────────────────────────────────

export function NavBtn({ label, badge, active, onClick, indent }) {
  const t = useT();
  return (
    <div style={{ padding: '1px 8px' }}>
      <button onClick={onClick} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: indent ? '5px 10px 5px 20px' : '7px 10px', border: 'none', borderRadius: 6,
        background: active ? t.accent : 'transparent', color: active ? '#fff' : t.muted,
        textAlign: 'left', cursor: 'pointer', fontSize: indent ? 12 : 13,
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        {badge !== undefined && (
          <span style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 11, background: active ? 'rgba(255,255,255,.2)' : t.chip.plain, padding: '1px 6px', borderRadius: 10 }}>{badge}</span>
        )}
      </button>
    </div>
  );
}

export function StatCard({ number, label }) {
  const t = useT();
  return (
    <div style={{ background: t.surf2, border: `1px solid ${t.border}`, borderRadius: 10, padding: '16px 18px' }}>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1, color: t.accentSoft }}>{number}</div>
      <div style={{ fontSize: 12, color: t.muted, marginTop: 4 }}>{label}</div>
    </div>
  );
}

export function InfoCard({ title, children, action }) {
  const t = useT();
  return (
    <div style={{ background: t.surf2, border: `1px solid ${t.border}`, borderRadius: 10, padding: '18px 20px', minWidth: 0, color: t.text }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: t.muted }}>{title}</div>
        {action && <div style={{ marginLeft: 'auto' }}>{action}</div>}
      </div>
      {children}
    </div>
  );
}

export function InfoRow({ label, value, mono, first }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', gap: 8, padding: '5px 0', fontSize: 13, borderTop: first ? 'none' : `1px solid ${t.border}` }}>
      <span style={{ color: t.muted, width: 190, flexShrink: 0 }}>{label}</span>
      <span style={{ wordBreak: 'break-word', fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined }}>{value}</span>
    </div>
  );
}

export function Heading({ title, sub, children, generated }) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, borderBottom: `1px solid ${t.border}`, paddingBottom: 10, flexWrap: 'wrap', color: t.text }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{title}{generated && <span title={GENERATED} style={{ color: t.warn }}> ✱</span>}</div>
        {sub && <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>{sub}</div>}
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

export function Note({ children, tone }) {
  const t = useT();
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.6, color: tone === 'warn' ? t.warn : t.muted, background: t.subtle, border: `1px solid ${t.border}`, borderRadius: 8, padding: '10px 14px' }}>
      {children}
    </div>
  );
}

// The blue "What this means" box.
export function Explain({ children }) {
  const t = useT();
  return (
    <div style={{ background: t.explainBg, border: `1px solid ${t.explainBorder}`, borderRadius: 8, padding: '12px 16px', fontSize: 13, lineHeight: 1.6, color: t.text }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: t.accentSoft, marginBottom: 6 }}>What this means</div>
      {children}
    </div>
  );
}

export function Chip({ children, tone }) {
  const t = useT();
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10, marginRight: 4, whiteSpace: 'nowrap',
      background: t.chip[tone] || t.chip.plain, color: tone === 'warn' ? t.warn : tone === 'good' ? t.good : t.muted,
    }}>{children}</span>
  );
}

// One line explaining the ✱, for pages that mix original data with a few generated parts.
export function GeneratedLegend({ children }) {
  const t = useT();
  return (
    <div style={{ fontSize: 11.5, color: t.muted }}>
      <span style={{ color: t.warn }}>✱</span> {children || `${GENERATED} The rest of this page comes from the return.`}
    </div>
  );
}

export function GeneratedBadge() {
  const t = useT();
  return (
    <div title={GENERATED} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start', fontSize: 11.5, fontWeight: 600, color: t.warn, background: t.badgeBg, border: `1px solid ${t.badgeBorder}`, borderRadius: 6, padding: '3px 9px' }}>
      ✱ {GENERATED}
    </div>
  );
}

// A searchable, sortable, paged table with CSV export. Columns: { key, label,
// text?, render?, sort?, mono?, color?, width?, maxWidth?, minWidth?, title?, generated? }.
export function DataTable({ columns, rows, exportName, initialSort, pageSize = 100, onRowClick, selectedKey, rowKey, emptyText, maxHeight }) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(initialSort || null);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [query, rows]);

  const text = (c, r) => (c.text ? c.text(r) : r[c.key]) ?? '';
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = q ? rows.filter(r => columns.some(c => String(text(c, r)).toLowerCase().includes(q))) : rows;
    if (sort) {
      const c = columns.find(x => x.key === sort.key);
      if (c) {
        const val = r => (c.sort ? c.sort(r) : text(c, r));
        out = [...out].sort((a, b) => {
          const x = val(a), y = val(b);
          if (x == null || x === '') return 1;
          if (y == null || y === '') return -1;
          const n = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
          return sort.dir === 'desc' ? -n : n;
        });
      }
    }
    return out;
  }, [rows, columns, query, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const b = btnStyle(t);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const shown = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: t.muted, borderBottom: `2px solid ${t.border}`, position: 'sticky', top: 0, background: t.surface, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0, color: t.text }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search these rows…"
          style={{ flex: '1 1 220px', maxWidth: 360, background: t.surf2, border: `1px solid ${t.border}`, borderRadius: 6, padding: '6px 10px', fontSize: 12.5, color: t.text, outline: 'none' }} />
        <span style={{ fontSize: 12, color: t.muted }}>
          {filtered.length === rows.length ? plural(rows.length, 'row') : `${filtered.length.toLocaleString()} of ${plural(rows.length, 'row')}`}
        </span>
        {exportName && rows.length > 0 && (
          <button style={{ ...b, marginLeft: 'auto' }} onClick={() => downloadCsv(exportName, columns, filtered)}>Export CSV</button>
        )}
      </div>
      <div style={{ overflow: 'auto', border: `1px solid ${t.border}`, borderRadius: 8, maxHeight: maxHeight || 'calc(100vh - 260px)', background: t.surface }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} title={[c.title, c.generated && `✱ ${GENERATED}`].filter(Boolean).join('\n')} style={{ ...th, width: c.width }}
                  onClick={() => setSort(s => (s && s.key === c.key ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: c.key, dir: 'asc' }))}>
                  {c.label}{c.generated ? <span style={{ color: t.warn }}> ✱</span> : ''}{c.title ? ' ⓘ' : ''}{sort?.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const key = rowKey ? rowKey(r) : i;
              return (
                <tr key={key} onClick={onRowClick ? () => onRowClick(r) : undefined}
                  style={{ cursor: onRowClick ? 'pointer' : undefined, background: selectedKey != null && key === selectedKey ? t.selected : undefined }}>
                  {columns.map(c => {
                    const v = c.render ? c.render(r) : text(c, r);
                    // Keep IDs, IPs and times on one line; let long text wrap.
                    const short = typeof v !== 'string' || v.length <= 40;
                    return (
                      <td key={c.key} style={{ padding: '6px 10px', borderBottom: `1px solid ${t.border}`, verticalAlign: 'top', fontFamily: c.mono ? 'monospace' : undefined, color: c.color, maxWidth: c.maxWidth || 420, minWidth: c.minWidth, whiteSpace: short ? 'nowrap' : 'normal', wordBreak: short ? undefined : 'break-word' }}>
                        {v}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!shown.length && (
              <tr><td colSpan={columns.length} style={{ padding: 20, textAlign: 'center', color: t.muted }}>{rows.length ? 'No rows match your search.' : (emptyText || 'No records.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: t.muted }}>
          <button style={b} disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          Page {page + 1} of {pages}
          <button style={b} disabled={page >= pages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
